import React from "react";
import {
  FiBarChart2,
  FiCalendar,
  FiClipboard,
  FiClock,
  FiDollarSign,
  FiFileText,
  FiGrid,
  FiInbox,
  FiList,
  FiMic,
  FiPhone,
  FiPhoneCall,
  FiRefreshCw,
  FiRepeat,
  FiSend,
  FiTool,
  FiTrendingUp,
  FiUsers,
  FiZap,
} from "react-icons/fi";
import { ROUTES } from "../app/routes";
import {
  isCommandCenterEnabled,
  isCommercialLinesRatingEnabled,
  isCommissionForecastEnabled,
  isCommunicationCenterEnabled,
  isDialerEnabled,
  isMacWorkflowsEnabled,
  isMarketplaceEnabled,
  isOpportunityTrackerEnabled,
  isPersonalLineRatingEnabled,
  isRenewalCenterEnabled,
} from "../utils/featureAccess";

/**
 * The Centers are the five places an agency works, in policy-lifecycle order.
 * Every workspace in the left nav belongs to exactly one Center, and this file
 * is the only place that mapping lives — the sidebar, the Center home pages and
 * the "hidden items" list all render from it.
 */

export interface CenterWorkspace {
  /** Route the workspace opens. Also its stable identity for hide/show prefs. */
  path: string;
  name: string;
  /** One line, plain language: what you come here to do. */
  blurb: string;
  icon: React.ReactNode;
  /** Where AI already helps inside this workspace. Shown as a quiet hint. */
  assist?: string;
  /** Gated workspaces resolve at render time, not module load. */
  isEnabled?: () => boolean;
  /** Treat descendant paths as active too (e.g. /voice/detail/123). */
  matchPrefix?: boolean;
}

export interface Center {
  id: string;
  name: string;
  /** Short label for tight spots (collapsed rail, breadcrumbs). */
  shortName: string;
  blurb: string;
  /** Theme token driving the Center's accent. Used sparingly, never as fill. */
  accentVar: string;
  icon: React.ReactNode;
  workspaces: CenterWorkspace[];
}

/** Command Center keeps its bespoke board glyph — it reads as a kanban at 14px. */
const CommandCenterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.6" y="0.6" width="13.8" height="13.8" rx="2" stroke="currentColor" strokeWidth="1" strokeOpacity="0.55" fill="none" />
    <rect x="0.6" y="0.6" width="13.8" height="3.6" rx="2" fill="currentColor" fillOpacity="0.18" />
    <line x1="0.6" y1="4.2" x2="14.4" y2="4.2" stroke="currentColor" strokeWidth="0.6" strokeOpacity="0.35" />
    <line x1="5.2" y1="4.2" x2="5.2" y2="14.4" stroke="currentColor" strokeWidth="0.6" strokeOpacity="0.3" />
    <line x1="9.8" y1="4.2" x2="9.8" y2="14.4" stroke="currentColor" strokeWidth="0.6" strokeOpacity="0.3" />
    <rect x="1.4" y="5.4" width="2.8" height="2.8" rx="0.6" fill="currentColor" />
    <rect x="1.4" y="9" width="2.8" height="2.4" rx="0.6" fill="currentColor" fillOpacity="0.45" />
    <rect x="5.7" y="5.4" width="2.8" height="2.4" rx="0.6" fill="currentColor" fillOpacity="0.7" />
    <rect x="10.3" y="5.4" width="2.8" height="2.4" rx="0.6" fill="currentColor" fillOpacity="0.5" />
  </svg>
);

/** Artifacts keeps its sparkle cluster — it's the one glyph users already know. */
const ArtifactsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 3L8.2 6.2L11.5 7.5L8.2 8.8L7 12L5.8 8.8L2.5 7.5L5.8 6.2L7 3Z" fill="currentColor" />
    <path d="M12 2L12.6 3.4L14 4L12.6 4.6L12 6L11.4 4.6L10 4L11.4 3.4L12 2Z" fill="currentColor" fillOpacity="0.6" />
    <path d="M11.5 9.5L12 10.7L13.2 11.2L12 11.7L11.5 12.8L11 11.7L9.8 11.2L11 10.7L11.5 9.5Z" fill="currentColor" fillOpacity="0.45" />
  </svg>
);

const CentersIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1.5" y="1.5" width="5.4" height="5.4" rx="1.4" stroke="currentColor" strokeWidth="1.1" />
    <rect x="9.1" y="1.5" width="5.4" height="5.4" rx="1.4" stroke="currentColor" strokeWidth="1.1" strokeOpacity="0.55" />
    <rect x="1.5" y="9.1" width="5.4" height="5.4" rx="1.4" stroke="currentColor" strokeWidth="1.1" strokeOpacity="0.55" />
    <rect x="9.1" y="9.1" width="5.4" height="5.4" rx="1.4" stroke="currentColor" strokeWidth="1.1" strokeOpacity="0.3" />
  </svg>
);

export const CENTERS: Center[] = [
  {
    id: "submission",
    name: "Submission Center",
    shortName: "Submission",
    blurb: "Rate it, quote it, get it bound.",
    accentVar: "--accent-blue",
    icon: <FiSend size={14} />,
    workspaces: [
      {
        path: ROUTES.CENTER_SUBMISSION_TRACKER,
        name: "Opportunity Tracker",
        blurb: "Every deal in market, with every carrier submission underneath it.",
        icon: <FiList size={14} />,
        assist: "Flags stale follow-ups and coverage gaps automatically",
        isEnabled: isOpportunityTrackerEnabled,
        matchPrefix: true,
      },
      {
        path: ROUTES.RATE_QUOTING,
        name: "Personal Lines Rating",
        blurb: "Rate personal auto and home across your carriers.",
        icon: <FiRefreshCw size={14} />,
        assist: "Pre-fills what it can from the insured record",
        isEnabled: isPersonalLineRatingEnabled,
        matchPrefix: true,
      },
      {
        path: ROUTES.COMMERCIAL_LINES_RATING,
        name: "Commercial Lines Rating",
        blurb: "Rate commercial risks and compare carrier appetite.",
        icon: <FiBarChart2 size={14} />,
        assist: "Flags carriers whose appetite fits the risk",
        isEnabled: isCommercialLinesRatingEnabled,
        matchPrefix: true,
      },
      {
        path: ROUTES.QUOTE_PROPOSAL,
        name: "Quote Proposal",
        blurb: "Build the proposal the client actually signs.",
        icon: <FiClipboard size={14} />,
        assist: "Drafts the proposal from your quotes",
        matchPrefix: true,
      },
    ],
  },
  {
    id: "servicing",
    name: "Servicing Center",
    shortName: "Servicing",
    blurb: "Look after the business already on the books.",
    accentVar: "--accent-cyan",
    icon: <FiMic size={14} />,
    workspaces: [
      {
        path: ROUTES.VOICE,
        name: "Momentum Voice",
        blurb: "Calls in and out, with transcripts against the record.",
        icon: <FiPhoneCall size={14} />,
        assist: "Answers, transcribes and summarizes calls",
        matchPrefix: true,
      },
      {
        path: ROUTES.CARRIER_DOWNLOAD,
        name: "Carrier Download",
        blurb: "Pull policy data down from carriers into the AMS.",
        icon: <FiFileText size={14} />,
        assist: "Reconciles downloads and surfaces mismatches",
      },
      {
        path: ROUTES.DIALER,
        name: "Dialer",
        blurb: "Place and manage calls straight from the record.",
        icon: <FiPhone size={14} />,
        matchPrefix: true,
        isEnabled: isDialerEnabled,
      },
    ],
  },
  {
    id: "renewal",
    name: "Renewal Center",
    shortName: "Renewal",
    blurb: "Keep the book, and grow it at renewal.",
    accentVar: "--accent-purple",
    icon: <FiRepeat size={14} />,
    workspaces: [
      {
        path: ROUTES.RENEWAL_CENTER,
        name: "Renewals",
        blurb: "Work the renewal book by what's closest and least certain.",
        icon: <FiCalendar size={14} />,
        assist: "Ranks the book by renewal risk",
        isEnabled: isRenewalCenterEnabled,
      },
    ],
  },
  {
    id: "billing",
    name: "Billing Center",
    shortName: "Billing",
    blurb: "Money in, money out, commissions reconciled.",
    accentVar: "--accent-green",
    icon: <FiTrendingUp size={14} />,
    workspaces: [
      {
        path: ROUTES.COMMISSION_FORECAST,
        name: "Commission Forecast",
        blurb: "See what's booked and what's coming.",
        icon: <FiDollarSign size={14} />,
        assist: "Projects commission from the in-force book",
        isEnabled: isCommissionForecastEnabled,
      },
    ],
  },
  {
    id: "management",
    name: "Management Center",
    shortName: "Management",
    blurb: "Run the agency: the work, the workforce, the tooling.",
    accentVar: "--accent-orange",
    icon: <FiGrid size={14} />,
    workspaces: [
      {
        path: ROUTES.MOMENTUM_ROUTINES,
        name: "Routines",
        blurb: "Recurring work that runs without you.",
        icon: <FiClock size={14} />,
        assist: "Runs on schedule and reports back",
        matchPrefix: true,
      },
      {
        path: ROUTES.MAC_WORKFLOWS,
        name: "MAC Workflows",
        blurb: "Build the automations your agency runs on.",
        icon: <FiZap size={14} />,
        assist: "Suggests next steps as you build",
        isEnabled: isMacWorkflowsEnabled,
        matchPrefix: true,
      },
      {
        path: ROUTES.DIGITAL_WORKFORCE,
        name: "Digital Workforce",
        blurb: "Your AI staff — what they own and how they're doing.",
        icon: <FiUsers size={14} />,
        assist: "Reports on its own throughput",
        matchPrefix: true,
      },
      {
        path: ROUTES.ARTIFACTS,
        name: "Artifacts",
        blurb: "Everything produced for you, in one place.",
        icon: <ArtifactsIcon />,
        assist: "Generated from your work as it happens",
      },
      {
        path: ROUTES.MARKETPLACE,
        name: "Toolbox",
        blurb: "Add skills and tools to the agency.",
        icon: <FiTool size={14} />,
        isEnabled: isMarketplaceEnabled,
      },
    ],
  },
];

/**
 * Command Center sits above the Centers, not inside one.
 *
 * It is where the day starts — every open task across the whole agency on one
 * board — so it is not a stage of the policy lifecycle and shouldn't be filed
 * behind one. It keeps the top of the nav, the position it held before the
 * Centers existed.
 */
export const PINNED_WORKSPACES: CenterWorkspace[] = [
  {
    path: ROUTES.MOMENTUM_COMMAND,
    name: "Command Center",
    blurb: "Every open task on one board, by who owns it.",
    icon: <CommandCenterIcon />,
    assist: "Routes and triages incoming work",
    isEnabled: isCommandCenterEnabled,
    matchPrefix: true,
  },
  {
    path: ROUTES.COMMUNICATION_CENTER,
    name: "Communication Center",
    blurb: "Every email and call thread, in one inbox.",
    icon: <FiInbox size={14} />,
    assist: "Surfaces what needs a reply first",
    isEnabled: isCommunicationCenterEnabled,
    matchPrefix: true,
  },
];

export const CENTERS_ICON = CentersIcon;

/** Path of a Center's home page. */
export const centerPath = (centerId: string) => `/center/${centerId}`;

export const getCenter = (centerId?: string): Center | undefined =>
  CENTERS.find(c => c.id === centerId);

/** Every workspace across all Centers, flattened — for the hidden-items list. */
export const ALL_WORKSPACES: Array<CenterWorkspace & { centerId: string }> = [
  ...PINNED_WORKSPACES.map(w => ({ ...w, centerId: "pinned" })),
  ...CENTERS.flatMap(center => center.workspaces.map(w => ({ ...w, centerId: center.id }))),
];

/** Respects feature flags. Call at render time. */
export const isWorkspaceEnabled = (w: CenterWorkspace): boolean => {
  const featureEnabled = w.isEnabled ? w.isEnabled() : true;
  if (localStorage.getItem('sourceTool') === 'Rate') {
    const isCL = localStorage.getItem('isCL') === 'true';
    const isPL = localStorage.getItem('isPL') === 'true';
    // Personal shows whenever it's flagged, or as the default when neither flag is set.
    if (w.path === ROUTES.RATE_QUOTING) return featureEnabled && (isPL || !isCL);
    if (w.path === ROUTES.COMMERCIAL_LINES_RATING) return featureEnabled && isCL;
    return false;
  }
  return featureEnabled;
};

/** The Center a route belongs to, so the sidebar can auto-open the right group. */
export const findCenterForPath = (pathname: string): Center | undefined => {
  // Pinned workspaces belong to no Center, so they open nothing.
  if (PINNED_WORKSPACES.some(w => isWorkspaceActive(w, pathname))) return undefined;
  const home = CENTERS.find(c => pathname === centerPath(c.id));
  if (home) return home;
  return CENTERS.find(c =>
    c.workspaces.some(w =>
      w.matchPrefix ? pathname === w.path || pathname.startsWith(w.path + "/") : pathname === w.path
    )
  );
};

export const isWorkspaceActive = (w: CenterWorkspace, pathname: string): boolean =>
  w.matchPrefix ? pathname === w.path || pathname.startsWith(w.path + "/") : pathname === w.path;

/** Name of the workspace (or Center) a route belongs to, e.g. "Command Center" —
 *  shown in the global chat bar and passed to the agent as context. */
export const findAreaNameForPath = (pathname: string): string | undefined => {
  const workspace = ALL_WORKSPACES.find(w => isWorkspaceActive(w, pathname));
  if (workspace) return workspace.name;
  return findCenterForPath(pathname)?.name;
};
